import clsx from "clsx";
import React from "react";

import { ListItem, type ListItemProps } from "@/components/atoms/ListItem";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./List.module.css";

type IconProps = Pick<ListItemProps, "icon"> & Pick<CommonProps, "color">;

const ref = "list";

interface ListProps extends Omit<CommonProps, "iconColor">, Partial<IconProps> {
  children: React.ReactNode;
  columnar?: boolean;
  compact?: boolean;
  iconTop?: boolean;
  inline?: boolean;
  ref?: React.Ref<HTMLUListElement>;
}

function mapChildren(props: ListProps, id: string) {
  const { children, color, icon } = props;
  const liProps: IconProps = { color, icon };

  return React.Children.toArray(children).map((child, index) => {
    const key = `${id}_${index}`;
    const { type } = child as React.ReactElement;

    if (type === ListItem) {
      return React.cloneElement(child as React.ReactElement, {
        key,
        ...liProps,
        ...(child as React.ReactElement<CommonProps>).props,
      });
    }

    return (
      <ListItem key={key} {...liProps}>
        {child}
      </ListItem>
    );
  });
}

function useList(props: ListProps) {
  const { color, columnar, id = createId(ref), iconTop, inline } = props;
  const children = mapChildren(props, id);
  const className = clsx(
    styles[ref],
    color && styles[color],
    columnar && !inline && children.length > 3 && styles.columnar,
    props.compact && styles.compact,
    iconTop && styles["icon-top"],
    inline && !columnar && styles.inline,
    props.className,
  );

  return {
    children,
    className,
    id,
  };
}

function List(props: ListProps) {
  const { children, className, id } = useList(props);

  return children.length ? (
    <ul ref={props.ref} className={className} id={id} role={props.role}>
      {children}
    </ul>
  ) : null;
}

export default List;
export { List, type ListProps };
